import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { fetchWithAuth } from '../../../../../utils/api/fetchWithAuth';
import { BASE_URL } from '../../../../../utils/Constants';
import { useAuthStore } from '../../../../../utils/stores/useAuthStore';
import { useChatNotificationsStore } from '../../../../../utils/stores/useChatNotificationsStore';
import { useSessionStore } from '../../../../../utils/stores/useSessionStore';
import { VenereChatSession } from '../types/VenereChatSession';

async function getVenereChatSessions(userId: string, idToken: string) {
  
  const response = await fetchWithAuth(`${BASE_URL}/api/chat/user/${userId}/sessions`, {
    method: 'GET',
    headers: {
      Authorization: idToken,
    },
  });

  if (response.status === 401) {
    useSessionStore.getState().setSessionExpired(true);
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch chat sessions');
  }
  
  const data = await response.json();
  return data as VenereChatSession[];
}

export const useVenereChatSessions = () => {
  const { auth } = useAuthStore();
  const userId = auth.userId;
  const idToken = auth.idToken;
  const queryClient = useQueryClient();
  const setUnreadSessions = useChatNotificationsStore(
    (state) => state.setUnreadSessions,
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['venereChatSessions', userId, idToken],
    queryFn: () => getVenereChatSessions(userId!, idToken!),
    enabled: !!userId && !!idToken,
    refetchInterval: 60 * 1000, // Refetch every minute
    staleTime: 60 * 1000, // Consider data stale after 1 minute
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  useEffect(() => {
    if (data) {
      // Check for unread messages
      const unreadSessions = data.filter(
        (session: VenereChatSession) =>
          new Date(session.last_message_sent_at) >
          new Date(session.last_read_user),
      );
      
      unreadSessions.forEach((session: VenereChatSession) => {
        queryClient.invalidateQueries({ queryKey: ['venereMessages', session.id] });
      });
      
      setUnreadSessions(unreadSessions);
    }
  }, [data, setUnreadSessions, queryClient, userId]);

  return { 
    data, 
    isLoading, 
    error, 
    refetch, 
    isFetching 
  };
}; 